/**
 * One broken screen must not take the whole app with it.
 *
 * Without this, an exception while rendering any page unmounted everything and
 * left a blank white window - no menu, no way back, nothing to tell anyone what
 * happened. Now the menu stays, the screen says what went wrong, and "Try
 * again" re-renders it. Navigating elsewhere resets it too (AppShell keys it on
 * the path).
 *
 * The commonest cause after a deploy is not a bug at all: a page left open
 * from the previous version asks for a code chunk the server no longer has.
 * That gets its own message - reload, and it is fixed.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface State {
  error: Error | null;
}

const STALE_BUILD = /dynamically imported module|Importing a module script failed|Loading chunk \S+ failed|error loading dynamically/i;

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Screen failed to render:', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = STALE_BUILD.test(error.message);
    return (
      <div className="mx-auto w-full max-w-xl px-4 py-10">
        <div role="alert" className="rounded-xl border border-border bg-surface p-5 shadow-xs">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-foreground">
                {stale ? 'A newer version of MouldTrack is available' : 'This screen ran into a problem'}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {stale
                  ? 'The server was updated while this page was open. Reload to continue - anything waiting to sync stays on this device.'
                  : 'Nothing you saved is affected - your data is on this device and the server. Try again, or reload the app.'}
              </p>
              {!stale ? (
                <details className="mt-3 text-xs text-muted-foreground">
                  <summary className="cursor-pointer">What went wrong</summary>
                  <p className="mt-1 break-words font-mono">{error.message || String(error)}</p>
                </details>
              ) : null}
              <div className="mt-4 flex flex-wrap gap-2">
                {!stale ? (
                  <button
                    type="button"
                    onClick={() => this.setState({ error: null })}
                    className="inline-flex h-9 items-center rounded-lg border border-border-strong bg-surface px-3 text-sm font-medium hover:bg-surface-sunken"
                  >
                    Try again
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
                >
                  <RefreshCw className="size-4" aria-hidden /> Reload
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
